import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import useAppSelector from "../../hooks/useAppSelector";
import { fetchRoads } from "../../redux/slices/road";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAuth from "../../hooks/useAuth";

import { MapComponent } from "./mapComponents/MapComponent";
import { fetchUsers } from "../../redux/slices/user";
import { fetchProperties } from "../../redux/slices/property";
import {
  fetchCandidates,
  approveCandidate,
  rejectCandidate,
  confirmCandidates,
  regenerateCandidates,
} from "../../redux/slices/propertyCandidates";
import CandidateReviewPanel from "./mapComponents/CandidateReviewPanel";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

export const isPropertyInUserAssociation = (
  propertyFeature: any,
  propertiesList: any[],
  associationId: number
): number | null => {
  const targetId = propertyFeature.properties?.kiinteistotunnuksenEsitysmuoto;
  const match = propertiesList.find((prop) => {
    const belongsToAssociation = prop.user?.associations?.some(
      (assoc: any) => assoc.id === associationId
    );
    return prop.code === targetId && belongsToAssociation;
  });
  return match?.id ?? null;
};

export const getPropertyById = (
  propertyId: any,
  properties: any[]
): any | null => {
  const match = properties.find((prop) => prop.id == propertyId);
  return match ?? null;
};

export const getMatchingFeature = (
  propertyFeature: any,
  propertiesList: any[],
  associationId: number
): any | null => {
  const targetId = propertyFeature.properties?.kiinteistotunnuksenEsitysmuoto;
  const match = propertiesList.find((prop) => {
    const belongsToAssociation = prop.user?.associations?.some(
      (assoc: any) => assoc.id === associationId
    );
    return prop.code === targetId && belongsToAssociation;
  });
  return match ?? null;
};

const MapPage: React.FC = () => {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isCandidateMode = searchParams.get("mode") === "candidates";

  const { status: roadStatus, roads } = useAppSelector((state) => state.road);
  const { status: propertyStatus, properties } = useAppSelector(
    (state) => state.property
  );
  const {
    candidates,
    pending,
    approved,
    total,
    bufferDistance,
    candidatesReviewed,
  } = useAppSelector((state) => state.propertyCandidates);

  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(
    null
  );
  const [confirming, setConfirming] = useState(false);
  const [skipForever, setSkipForever] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  useEffect(() => {
    if (!associationId) return;
    dispatch(fetchRoads(associationId));
    dispatch(fetchProperties(associationId));
    dispatch(fetchUsers({ associationId }));
    if (isCandidateMode) {
      dispatch(fetchCandidates(associationId));
    }
  }, [dispatch, associationId, isCandidateMode]);

  const handleApprove = useCallback(
    (candidateId: number) => {
      if (!associationId) return;
      dispatch(approveCandidate({ associationId, candidateId }));
    },
    [dispatch, associationId]
  );

  const handleReject = useCallback(
    (candidateId: number) => {
      if (!associationId) return;
      dispatch(rejectCandidate({ associationId, candidateId }));
    },
    [dispatch, associationId]
  );

  const handleBulkApproveHigh = useCallback(() => {
    if (!associationId) return;
    candidates
      .filter((c) => c.confidence === "HIGH" && c.status === "PENDING_REVIEW")
      .forEach((c) =>
        dispatch(approveCandidate({ associationId, candidateId: c.id }))
      );
  }, [dispatch, associationId, candidates]);

  const handleConfirm = useCallback(async () => {
    if (!associationId) return;
    setConfirming(true);
    await dispatch(confirmCandidates(associationId));
    setConfirming(false);
  }, [dispatch, associationId]);

  const handleChangeBuffer = useCallback(
    (d: 25 | 50) => {
      if (!associationId) return;
      dispatch(regenerateCandidates({ associationId, bufferDistance: d }));
    },
    [dispatch, associationId]
  );

  const isLoading =
    (roadStatus === "loading" || propertyStatus === "loading") &&
    !isCandidateMode;

  return (
    <>
      <Helmet title={isCandidateMode ? "Kiinteistöehdotukset" : "Kartta"} />
      <Typography variant="h3" gutterBottom display="inline">
        {isCandidateMode ? "Kiinteistöehdotukset" : "Kartta"}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" sx={{ mt: 2 }}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Kartta</Typography>
        {isCandidateMode && <Typography>Kiinteistöehdotukset</Typography>}
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="50vh"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box position="relative">
              <MapComponent
                roads={roads}
                properties={properties}
                type={isCandidateMode ? "candidateReview" : "properties"}
                candidates={isCandidateMode ? candidates : undefined}
                onCandidateClick={
                  isCandidateMode ? setSelectedCandidateId : undefined
                }
              />
              {isCandidateMode && !candidatesReviewed && (
                <CandidateReviewPanel
                  candidates={candidates}
                  pending={pending}
                  approved={approved}
                  total={total}
                  confirming={confirming}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onBulkApproveHigh={handleBulkApproveHigh}
                  onConfirm={handleConfirm}
                  selectedCandidateId={selectedCandidateId}
                  bufferDistance={bufferDistance as 25 | 50}
                  onChangeBuffer={handleChangeBuffer}
                  skipForever={skipForever}
                  onSkipForeverChange={setSkipForever}
                />
              )}
              {isCandidateMode && candidatesReviewed && (
                <Box
                  position="absolute"
                  top={12}
                  right={12}
                  bgcolor="background.paper"
                  p={2}
                  borderRadius={2}
                  boxShadow={4}
                  zIndex={10}
                >
                  <Typography variant="body2">
                    Kiinteistöehdotukset on jo vahvistettu.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default MapPage;
